import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { NotificationsListResponse } from "@situate/contracts/runtime";
import type {
  AlertStatus,
  NotificationSeverity,
  NotificationType,
} from "@situate/contracts/firestore";
import { notificationsListResponseSchema } from "@situate/contracts/runtime-zod";
import { useSelectedDeployment } from "@/features/deployment-selection/hooks/use-selected-deployment";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";

export interface NotificationFeedFilters {
  severity: NotificationSeverity | null;
  notificationType: NotificationType | null;
  lifecycleStatus: AlertStatus | null;
}

export type NotificationsFeedStatus = "unavailable" | "loading" | "error" | "empty" | "ready";

interface NotificationsFeedState {
  filters: NotificationFeedFilters;
  notifications: NotificationsListResponse["notifications"];
  status: NotificationsFeedStatus;
  error: Error | null;
  onFiltersChange: (filters: NotificationFeedFilters) => void;
}

const NOTIFICATIONS_PATH = "/v1/notifications";

function buildNotificationsPath(filters: NotificationFeedFilters): string {
  const params = new URLSearchParams();
  if (filters.notificationType !== null) params.set("type", filters.notificationType);
  if (filters.severity !== null) params.set("severity", filters.severity);
  if (filters.lifecycleStatus !== null) params.set("alert_status", filters.lifecycleStatus);
  const query = params.toString();
  return query === "" ? NOTIFICATIONS_PATH : `${NOTIFICATIONS_PATH}?${query}`;
}

export function useNotificationsFeed(): NotificationsFeedState {
  const { deploymentId } = useSelectedDeployment();
  const runtime = useRuntimeHttpClient();
  const [filters, setFilters] = useState<NotificationFeedFilters>({
    severity: null,
    notificationType: null,
    lifecycleStatus: "active",
  });
  const hasNotificationsGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "notifications_list"
  );
  const enabled =
    runtime.client !== null && runtime.deploymentId === deploymentId && hasNotificationsGrant;
  const query = useQuery({
    queryKey: [
      "notifications",
      deploymentId,
      filters.notificationType,
      filters.severity,
      filters.lifecycleStatus,
    ],
    queryFn: async (): Promise<NotificationsListResponse> => {
      if (runtime.client === null) throw new Error("Runtime client is unavailable");
      const rawResponse = await runtime.client.get<unknown>(buildNotificationsPath(filters));
      return notificationsListResponseSchema.parse(rawResponse);
    },
    enabled,
  });

  let status: NotificationsFeedStatus;
  if (!enabled) status = "unavailable";
  else if (query.isLoading) status = "loading";
  else if (query.isError) status = "error";
  else if ((query.data?.notifications.length ?? 0) === 0) status = "empty";
  else status = "ready";

  return {
    filters,
    notifications: query.data?.notifications ?? [],
    status,
    error: query.error instanceof Error ? query.error : null,
    onFiltersChange: setFilters,
  };
}
